import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext } from 'react';
import { APIError, userQuery } from '../queries';

type Role = 'ADMIN' | 'BETA_TESTER';

type User = {
  userName: string;
  isContributor: boolean;
  initiativesLed: string[];
  initiativesManaged: string[];
  roles: Role[];
};

type UserStatus = 'success' | 'loading' | 'error';

type UserContextSuccess = {
  type: 'success';
  user: User;
};

type UserContextLoading = {
  type: 'loading';
  isLoading: boolean;
};

type AppError = {
  type: string;
  title: string;
  message: string;
  cause: string | unknown | undefined;
  stack: string | undefined;
};
// FIXME Improve typing for error
type UserContextError = {
  type: 'error';
  error: AppError | APIError;
};

type UserContextType =
  | UserContextSuccess
  | UserContextLoading
  | UserContextError;

const UserContext = createContext<null | UserContextType>(null);

export const useUser = () => {
  const userCxt = useContext(UserContext);

  if (!userCxt) {
    throw new Error(
      'The useUserContext hook can only be used within UserProvider.',
    );
  }

  if (userCxt.type === 'loading') {
    return {
      user: null,
      isLoading: true,
      isContributor: false,
      isAdmin: false,
      isLoggedIn: false,
      userError: null,
    };
  }

  if (userCxt.type === 'error') {
    return {
      user: null,
      isLoading: false,
      isContributor: false,
      isAdmin: false,
      isLoggedIn: false,
      userError: userCxt.error,
    };
  }
  const isAdmin = userCxt.user.roles.includes('ADMIN');
  const isContributor = userCxt.user.isContributor;
  return {
    user: userCxt.user,
    isContributor: isContributor,
    isAdmin: isAdmin,
    isLoggedIn: true,
    isLoading: false,
    userError: null,
  };
};

/**
 * This hooks manage a big part of the business logic
 */

type UserAccessRights = {
  canFollowThematique: boolean;
  canFollowInitiative: boolean;
  canContactPilot: boolean;
  canCreateIAInitiative: boolean;
  canCreateDomaineInitiative: boolean;
};
export const useUserAccessRights = (): UserAccessRights => {
  const { user, isAdmin, isContributor, isLoggedIn } = useUser();
  if (!isLoggedIn || !isContributor) {
    return {
      canFollowThematique: false,
      canFollowInitiative: false,
      canContactPilot: false,
      canCreateIAInitiative: false,
      canCreateDomaineInitiative: false,
    };
  }

  if (isAdmin) {
    return {
      canFollowThematique: true,
      canFollowInitiative: true,
      canContactPilot: true,
      canCreateIAInitiative: true,
      canCreateDomaineInitiative: true,
    };
  }

  const isManagerOfInitiative =
    (user && user?.initiativesManaged?.length > 0) ?? false;

  const loggedInUserAccessRights = {
    canFollowThematique: isContributor,
    canFollowInitiative: isContributor,
    canContactPilot: isContributor,
    canCreateIAInitiative: isContributor,
    canCreateDomaineInitiative: isManagerOfInitiative,
  };

  return loggedInUserAccessRights;
};

type InitiativeAccessRights = {
  canContactPilot: boolean;
  canEditInitiative: boolean;
};
export const useUserInitiativeAccessRights = (
  initiativeId: string,
): InitiativeAccessRights => {
  const { user, isAdmin, isContributor, isLoggedIn } = useUser();
  if (!isLoggedIn || !isContributor) {
    return {
      canContactPilot: false,
      canEditInitiative: false,
    };
  }

  if (isAdmin) {
    return {
      canContactPilot: true,
      canEditInitiative: true,
    };
  }

  const isManagerOfInitiative =
    user?.initiativesManaged.includes(initiativeId) ?? false;

  const loggedInUserAccessRights = {
    canContactPilot: true,
    canEditInitiative: isManagerOfInitiative,
  };

  return loggedInUserAccessRights;
};

export const UserProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const queryData = useQuery({
    ...userQuery,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  let contextValue: UserContextType = { type: 'loading', isLoading: true };
  if (queryData.isError) {
    if (queryData?.error?.json?.error) {
      const err = queryData.error.json;
      contextValue = {
        type: 'error',
        error: {
          type: err.type,
          title: err.title,
          message: err.message,
          cause: err.cause,
          stack: err.stack,
        },
      };
    } else {
      contextValue = { type: 'error', error: queryData.error };
    }
  }

  if (queryData.isSuccess) {
    contextValue = { type: 'success', user: queryData.data };
  }

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};
